// src/admin/screens/borrowers/tabs/LoansTab.tsx
// Lists every loan on this borrower. Clicks drill into the same
// LoanDetailScreen used from the queue, so the agent keeps a single
// mental model for loan inspection.
import { useNavigate } from 'react-router-dom';
import DataTable, { type Column } from '@admin/components/DataTable';
import StatusChip from '@admin/components/StatusChip';
import Money from '@admin/components/Money';
import { LOAN_STATUS_LABEL, LOAN_STATUS_TONE } from '@admin/utils/loanStatus';
import { formatCatDate } from '@admin/utils/dates';
import type { Ngwee } from '@/lib/types';
import type { BorrowerDetail } from '@admin/hooks/useBorrower';

type LoanRow = BorrowerDetail['loans'][number];

export default function LoansTab({ borrower }: { borrower: BorrowerDetail }) {
  const navigate = useNavigate();
  const rows: LoanRow[] = [...(borrower.loans ?? [])].sort((a, b) =>
    a.applied_at < b.applied_at ? 1 : -1,
  );

  const cols: Column<LoanRow>[] = [
    { key: 'no', header: 'Loan', render: (r) => <strong>{r.loan_number}</strong> },
    {
      key: 'st',
      header: 'Status',
      render: (r) => (
        <StatusChip label={LOAN_STATUS_LABEL[r.status]} tone={LOAN_STATUS_TONE[r.status]} />
      ),
    },
    {
      key: 'p',
      header: 'Principal',
      align: 'right',
      render: (r) => <Money amount={r.principal_ngwee as Ngwee} />,
    },
    {
      key: 't',
      header: 'Total repayable',
      align: 'right',
      render: (r) =>
        r.total_repayable_ngwee != null ? <Money amount={r.total_repayable_ngwee as Ngwee} /> : '—',
    },
    { key: 'a', header: 'Applied', render: (r) => formatCatDate(r.applied_at) },
    { key: 'd', header: 'Due', render: (r) => (r.due_date ? formatCatDate(r.due_date) : '—') },
  ];

  return (
    <DataTable
      rows={rows}
      columns={cols}
      rowKey={(r) => r.id}
      onRowClick={(r) => navigate(`/loans/${r.id}`)}
      emptyLabel="No loans yet."
    />
  );
}
